import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart,
  CheckCircle,
  Check,
  Clock,
  Bookmark,
  ThumbsUp,
  CalendarCheck,
  FileText,
  Users,
  Calendar,
  RotateCcw,
  Lock,
  Mail,
  Phone,
  AlertCircle as ExclamationCircle,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import ProgramSessionForm from '@/components/camp/ProgramSessionForm';
import { getCurrentSessionYear } from '@/utils/sessionExpiryHelpers';
import SessionList from '@/components/programs/SessionList';
import AddSessionModal from '@/components/programs/modal/AddSessionModal';
import { UserProgram } from '@/api/entities';
import { getCurrentUser, supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

/**
 * Edit Program Form Component
 * 
 * Form for editing user program details with status selection and accordion sections
 * 
 * @param {Object} props
 * @param {Object} props.userProgram - User program data
 * @param {Object} props.camp - Camp data
 * @param {Function} props.onSave - Save handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {Object} [props.profile] - User profile data with children
 * @param {boolean} [props.isSaving] - Loading state
 */
const EditProgramForm = forwardRef(({
  userProgram,
  camp,
  onSave,
  onCancel,
  profile = null,
  isSaving = false,
  /**
   * sections controls which portions of the form are rendered:
   * - 'all' (default): public + private sections
   * - 'public': only public sections (basic info, dates, times, refund policy)
   * - 'private': only private sections (status, booking info, additional notes)
   */
  sections = 'all',
}, ref) => {
  const { toast } = useToast();
  // Helper function to normalize date strings to YYYY-MM-DD format
  // Extracts date part directly to avoid timezone conversion issues
  const normalizeDateString = (dateValue) => {
    if (!dateValue) return '';
    
    // If it's already a clean YYYY-MM-DD string, return as-is (no timezone conversion needed)
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue.trim())) {
      return dateValue.trim();
    }
    
    // Extract YYYY-MM-DD from ISO strings or other date formats
    try {
      // Match YYYY-MM-DD pattern at the start of the string (handles ISO format like "2024-06-15T00:00:00Z")
      const dateMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        // Validate date components
        const yearNum = parseInt(year, 10);
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        
        // Validate the date is reasonable
        if (yearNum >= 1900 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
          // Return the date string directly without any Date object conversion
          return `${year}-${month}-${day}`;
        }
      }
      
      // If it's a Date object, use UTC methods to avoid timezone shifts
      if (dateValue instanceof Date) {
        if (isNaN(dateValue.getTime())) return '';
        // Use UTC methods to get the date components without timezone conversion
        const year = dateValue.getUTCFullYear();
        const month = String(dateValue.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      // Try parsing as Date and use UTC methods
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch {
      return '';
    }
    
    return '';
  };

  // Helper function to sanitize dates before saving - ensures YYYY-MM-DD format
  // This prevents any timezone conversion issues when saving to database
  // IMPORTANT: Date inputs return YYYY-MM-DD format directly, so we preserve it exactly
  const sanitizeDateForSave = (dateValue) => {
    // Handle all falsy values (null, undefined, empty string, 0, false)
    if (!dateValue || dateValue === null || dateValue === undefined) {
      return '';
    }
    
    // Convert to string and handle empty/whitespace
    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      // If it's empty or whitespace, return empty string
      if (trimmed === '') {
        return '';
      }
      
      // If it's already a clean YYYY-MM-DD string, return as-is (most common case from date inputs)
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }
    }
    
    // For any other format, try to extract YYYY-MM-DD without creating Date objects
    // Extract date part from ISO string (YYYY-MM-DDTHH:mm:ss or YYYY-MM-DD)
    try {
      const dateStr = String(dateValue);
      const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        // Validate the date components without creating a Date object (avoids timezone issues)
        const yearNum = parseInt(year, 10);
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        if (yearNum >= 1900 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
          // Return the extracted date string directly - no Date object creation
          return `${year}-${month}-${day}`;
        }
      }
    } catch (error) {
      // If parsing fails, return empty string to avoid invalid data
      console.warn('Error sanitizing date value:', dateValue, error);
      return '';
    }
    
    // If we can't parse it, return empty string to avoid invalid data
    console.warn('Could not sanitize date value:', dateValue);
    return '';
  };

  // Initialize form data from userProgram or camp (for email-scout mode)
  const programData = userProgram?.program_data || {};
  
  // For email-scout mode without linked UserProgram, use camp data
  const emailScoutData = !userProgram && camp ? {
    name: camp.name,
    organization: camp.organization,
    location: camp.location || camp.formatted_address || camp.general_location,
    age_range: camp.age_range,
    grade_range: camp.grade_range,
    camp_start_date: camp.camp_start_date || camp.start_date,
    camp_end_date: camp.camp_end_date || camp.end_date,
    start_date: camp.start_date || camp.camp_start_date,
    end_date: camp.end_date || camp.camp_end_date,
    session_start_time: camp.session_start_time || camp.user_session_start_time,
    session_end_time: camp.session_end_time || camp.user_session_end_time,
    registration_open_date: camp.registration_open_date,
    early_registration_open_date: camp.early_registration_open_date,
    registration_deadline: camp.registration_deadline,
    early_bird_deadline: camp.early_bird_deadline,
    cancellation_deadline: camp.cancellation_deadline,
    payment_due_date: camp.payment_due_date,
    price: camp.price || camp.cost_total || camp.cost,
    cost_total: camp.cost_total || camp.price,
    website: camp.website || camp.website_url,
    website_url: camp.website_url || camp.website,
    phone: camp.phone || camp.contact_phone,
    contact_phone: camp.contact_phone || camp.phone,
    email: camp.contact_email || camp.email,
    contact_email: camp.contact_email || camp.email,
    refund_policy: camp.refund_policy,
    cancellation_policy: camp.cancellation_policy,
    camp_activities: camp.camp_activities || camp.activities,
    items_to_bring: camp.items_to_bring,
    special_instructions: camp.special_instructions,
    medical_requirements: camp.medical_requirements,
    child_names: camp.child_names || (camp.child_name ? [camp.child_name] : []),
    child_age: camp.child_age
  } : {};

  // Merge programData with emailScoutData (emailScoutData takes precedence for email-scout mode)
  const mergedProgramData = { ...programData, ...emailScoutData };

  // Helper to normalize status to array format
  const normalizeStatusToArray = (status) => {
    if (!status) return ['saved']; // Default to saved
    if (Array.isArray(status)) {
      return status.filter(Boolean).length > 0 ? status.filter(Boolean) : ['saved'];
    }
    return [String(status)];
  };

  // Get initial status - check both status field and program_data.selected_statuses
  const getInitialStatus = () => {
    const statuses = new Set();
    
    // Check userProgram.status (can be string or array)
    if (Array.isArray(userProgram?.status)) {
      userProgram.status.forEach(s => s && statuses.add(s.toLowerCase()));
    } else if (userProgram?.status) {
      statuses.add(String(userProgram.status).toLowerCase());
    }
    
    // Check program_data.selected_statuses
    const selectedStatuses = mergedProgramData.selected_statuses;
    if (Array.isArray(selectedStatuses)) {
      selectedStatuses.forEach(s => s && statuses.add(s.toLowerCase()));
    } else if (selectedStatuses) {
      statuses.add(String(selectedStatuses).toLowerCase());
    }
    
    // For email-scout mode, check camp email_source.user_status
    if (!userProgram && camp?.email_source?.user_status) {
      const emailStatus = camp.email_source.user_status;
      if (emailStatus === 'favorited') {
        statuses.add('favorite');
      } else if (emailStatus === 'saved_for_later') {
        statuses.add('saved');
      }
    }
    
    return Array.from(statuses).length > 0 ? Array.from(statuses) : ['saved'];
  };

  const [formData, setFormData] = useState({
    // Status (required field) - now an array to support multiple selections
    status: getInitialStatus(),

    // Basic Information
    name: mergedProgramData.name || camp?.name || '',
    organization: mergedProgramData.organization || camp?.organization || '',
    location: mergedProgramData.location || camp?.location || camp?.formatted_address || camp?.general_location || '',
    age_range: mergedProgramData.age_range || mergedProgramData.ageRange || camp?.age_range || '',
    grade_range: mergedProgramData.grade_range || camp?.grade_range || '',
    description: mergedProgramData.description || mergedProgramData.editorial_summary || mergedProgramData.brief_description || camp?.editorial_summary || camp?.description || camp?.brief_description || '',

    // Children
    child_names: userProgram?.child_names || mergedProgramData.child_names || camp?.child_names || (camp?.child_name ? [camp.child_name] : []) || [],
    selectedChildren: [], // Will be populated from child_names

    // Dates - normalize to YYYY-MM-DD format to prevent timezone issues
    camp_start_date: normalizeDateString(mergedProgramData.camp_start_date || mergedProgramData.campStartDate || mergedProgramData.start_date || camp?.camp_start_date || camp?.start_date || ''),
    camp_end_date: normalizeDateString(mergedProgramData.camp_end_date || mergedProgramData.campEndDate || mergedProgramData.end_date || camp?.camp_end_date || camp?.end_date || ''),
    registration_open_date: normalizeDateString(mergedProgramData.registration_open_date || mergedProgramData.registrationOpenDate || camp?.registration_open_date || ''),
    early_registration_open_date: normalizeDateString(mergedProgramData.early_registration_open_date || mergedProgramData.earlyRegistrationOpenDate || camp?.early_registration_open_date || ''),
    registration_deadline: normalizeDateString(mergedProgramData.registration_deadline || mergedProgramData.registrationDeadline || camp?.registration_deadline || ''),
    early_bird_deadline: normalizeDateString(mergedProgramData.early_bird_deadline || mergedProgramData.earlyBirdDeadline || camp?.early_bird_deadline || ''),
    cancellation_deadline: normalizeDateString(mergedProgramData.cancellation_deadline || mergedProgramData.cancellationDeadline || camp?.cancellation_deadline || ''),

    // Schedule
    session_start_time: mergedProgramData.session_start_time || mergedProgramData.user_session_start_time || camp?.session_start_time || camp?.user_session_start_time || '',
    session_end_time: mergedProgramData.session_end_time || mergedProgramData.user_session_end_time || camp?.session_end_time || camp?.user_session_end_time || '',
    schedule_text: mergedProgramData.schedule_text || mergedProgramData.schedule_dates || camp?.schedule_dates || '',

    // Pricing
    price: mergedProgramData.price || mergedProgramData.cost || mergedProgramData.regularPrice || mergedProgramData.cost_structure || mergedProgramData.cost_total || camp?.price || camp?.cost_total || camp?.cost || '',
    price_period: mergedProgramData.price_period || mergedProgramData.pricePer || 'week',
    price_notes: mergedProgramData.price_notes || mergedProgramData.cost_notes || '',
    early_bird_price: mergedProgramData.early_bird_price || mergedProgramData.earlyBirdPrice || '',

    // Contact
    website: mergedProgramData.website || mergedProgramData.website_url || camp?.website_url || camp?.website || '',
    phone: mergedProgramData.phone || mergedProgramData.contact_phone || camp?.contact_phone || camp?.phone || '',
    email: mergedProgramData.email || mergedProgramData.contact_email || camp?.contact_email || camp?.email || '',

    // Additional
    refund_policy: mergedProgramData.refund_policy || mergedProgramData.refundPolicy || camp?.refund_policy || '',
    cancellation_policy: mergedProgramData.cancellation_policy || camp?.cancellation_policy || '',
    notes: mergedProgramData.notes || camp?.email_source?.user_notes || '',
    booking_confirmation: mergedProgramData.booking_confirmation || '',
    source: mergedProgramData.source || (camp?.email_source ? 'email_scout' : 'manual'),
    children_pricing: mergedProgramData.children_pricing || {},
    // Program/Session hierarchy fields
    program: mergedProgramData.program || camp?.program || '',
    session: mergedProgramData.session || camp?.session || '',
    sessionYear: mergedProgramData.session_year || camp?.session_year || null,
    session_price: mergedProgramData.session_price || null,
  });

  const [errors, setErrors] = useState({});
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);

  // Check if current record is a base camp (parent_camp_id is null) or a session
  const isBaseCamp = !userProgram?.parent_camp_id;
  const baseCampId = isBaseCamp ? userProgram?.id : userProgram?.parent_camp_id;

  // PHASE 1: Detect camp type and get allowed statuses
  // Detect if this is a parent camp (not a session)
  const isParentCamp = !camp?.hierarchy_level || 
                       camp?.hierarchy_level === 'camp' ||
                       (!camp?.parent_place_id && camp?.hierarchy_level !== 'session') ||
                       (!userProgram?.parent_camp_id && !camp?.session);

  // Detect if this is a session
  const isSession = camp?.hierarchy_level === 'session' || 
                    camp?.parent_place_id || 
                    userProgram?.parent_camp_id ||
                    camp?.session;

  // Get allowed statuses based on camp type
  const getAllowedStatuses = () => {
    const base = ['saved', 'recommended', 'favorite', 'considering'];
    return isSession ? [...base, 'booked', 'attended'] : base;
  };

  // Check if booking info should show
  // For parent camps: only show when status is "booked"
  // For sessions: show for both "considering" and "booked"
  const showBookingInfo = () => {
    const statuses = Array.isArray(formData.status) ? formData.status : [formData.status].filter(Boolean);
    
    // For parent camps, only show booking info when status is "booked"
    if (isParentCamp) {
      return statuses.some(s => String(s).toLowerCase() === 'booked');
    }
    
    // For sessions, show for both "considering" and "booked"
    return statuses.some(s => 
      String(s).toLowerCase() === 'considering' || 
      String(s).toLowerCase() === 'booked'
    );
  };

  // Check if sessions list should show (for saved/recommended/favorite, or parent camp when considering)
  const showSessionsList = () => {
    const statuses = Array.isArray(formData.status) ? formData.status : [formData.status].filter(Boolean);
    const hasListStatus = statuses.some(s => {
      const status = String(s).toLowerCase();
      return status === 'saved' || status === 'recommended' || status === 'favorite';
    });
    const isConsideringParent = isBaseCamp && baseCampId && statuses.some(s => String(s).toLowerCase() === 'considering');
    return hasListStatus || isConsideringParent;
  };

  // Helper function to enrich sessions with status from user_programs
  const enrichSessionsWithStatus = React.useCallback(async (sessions, userId, baseCampId) => {
    if (!userId || !sessions.length || !baseCampId) return sessions;
    
    try {
      // Query user_programs for all sessions under this parent camp
      const { data: userPrograms, error } = await supabase
        .from('user_programs')
        .select('id, status, program_data, child_names')
        .eq('user_id', userId)
        .eq('parent_camp_id', baseCampId);
      
      if (error) {
        console.error('Error loading user_programs for status enrichment:', error);
        return sessions;
      }
      
      if (!userPrograms || userPrograms.length === 0) return sessions;
      
      // Create map for quick lookup
      // Match by place_id (for community sessions) or by session name + year
      const statusMap = new Map();
      userPrograms.forEach(up => {
        const pd = typeof up.program_data === 'string' 
          ? JSON.parse(up.program_data) 
          : up.program_data || {};
        
        // Use place_id as primary key
        const placeIdKey = pd.place_id;
        if (placeIdKey) {
          statusMap.set(placeIdKey, {
            status: up.status,
            child_names: up.child_names,
            id: up.id,
            program_data: pd
          });
        }
        
        // Also index by session name + year for fallback matching
        const sessionName = pd.session || pd.session_name;
        const sessionYear = pd.session_year;
        if (sessionName && sessionYear) {
          const sessionKey = `${sessionName}-${sessionYear}`;
          // Only set sessionKey if placeIdKey wasn't set (for sessions without place_id)
          if (!placeIdKey) {
            statusMap.set(sessionKey, {
              status: up.status,
              child_names: up.child_names,
              id: up.id,
              program_data: pd
            });
          }
        }
      });
      
      // Merge status into sessions
      return sessions.map(session => {
        const sessionPlaceId = session.place_id || session.id;
        const sessionName = session.session || session.program_data?.session;
        const sessionYear = session.session_year || session.program_data?.session_year;
        const sessionKey = sessionName && sessionYear ? `${sessionName}-${sessionYear}` : null;
        
        // Try to find matching user_program entry
        let userProgram = statusMap.get(sessionPlaceId);
        if (!userProgram && sessionKey) {
          userProgram = statusMap.get(sessionKey);
        }
        
        if (userProgram) {
          return {
            ...session,
            status: userProgram.status,
            child_names: userProgram.child_names || session.child_names,
            user_program_id: userProgram.id,
            // Merge program_data to include user-specific data
            program_data: {
              ...session.program_data,
              ...userProgram.program_data
            }
          };
        }
        return session;
      });
    } catch (error) {
      console.error('Error enriching sessions with status:', error);
      return sessions;
    }
  }, []);

  // Shared helper: load sessions from both user_programs and camp_audit_data
  const loadSessions = React.useCallback(async () => {
    if (!isBaseCamp || !baseCampId) {
      setSessions([]);
      return;
    }
    setIsLoadingSessions(true);
    try {
      const user = await getCurrentUser();
      const allSessions = [];
      const parentPlaceId = camp?.place_id || camp?.id || userProgram?.program_data?.place_id;

      if (user?.id) {
        try {
          const userSessionList = await UserProgram.getSessionsForCamp(user.id, baseCampId);
          if (userSessionList?.length) allSessions.push(...userSessionList);
        } catch (e) {
          console.error('Error loading user sessions:', e);
        }
      }
      if (parentPlaceId) {
        try {
          const { getCampSessions } = await import('@/services/campHierarchyService');
          const communitySessions = await getCampSessions(parentPlaceId, { includeExpired: false });
          if (communitySessions?.length) {
            const formatted = communitySessions.map(s => ({
              id: s.id,
              program_data: {
                name: s.name,
                organization: s.organization || '',
                session: s.session,
                session_year: s.session_year,
                // Include both DB field names and legacy names for compatibility
                user_camp_start_date: s.user_camp_start_date,
                user_camp_end_date: s.user_camp_end_date,
                camp_start_date: s.user_camp_start_date || s.start_date,
                camp_end_date: s.user_camp_end_date || s.end_date,
                start_date: s.user_camp_start_date || s.start_date,
                end_date: s.user_camp_end_date || s.end_date,
                cost: s.cost,
                price: s.cost ?? s.price ?? s.session_price,
                location: s.location || s.formatted_address || '',
                place_id: s.place_id,
              },
              name: s.name,
              organization: s.organization,
              session: s.session,
              session_year: s.session_year,
              // ✅ Include correct database field names at top level
              user_camp_start_date: s.user_camp_start_date,
              user_camp_end_date: s.user_camp_end_date,
              start_date: s.user_camp_start_date || s.start_date,
              end_date: s.user_camp_end_date || s.end_date,
              cost: s.cost,
              place_id: s.place_id,
              from_camp_audit_data: true,
            }));
            allSessions.push(...formatted);
          }
        } catch (e) {
          console.error('Error loading community sessions:', e);
        }
      }
      
      // Deduplicate sessions
      let unique = Array.from(new Map(allSessions.map(s => [s.id, s])).values());
      
      // Enrich sessions with status from user_programs
      if (user?.id) {
        unique = await enrichSessionsWithStatus(unique, user.id, baseCampId);
      }
      
      setSessions(unique);
    } catch (error) {
      console.error('Error loading sessions:', error);
      setSessions([]);
    } finally {
      setIsLoadingSessions(false);
    }
  }, [isBaseCamp, baseCampId, camp?.place_id, camp?.id, userProgram?.program_data?.place_id, enrichSessionsWithStatus]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Derived visibility flags for public vs private sections
  const showPublicSections = sections === 'all' || sections === 'public';
  const showPrivateSections = sections === 'all' || sections === 'private';

  // Validation helper functions
  const isValidUrl = (string) => {
    if (!string || string.trim() === '') return true; // Empty is valid (optional field)
    try {
      // Add protocol if missing
      let urlString = string.trim();
      if (!urlString.startsWith('http://') && !urlString.startsWith('https://')) {
        urlString = `https://${urlString}`;
      }
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const isValidEmail = (email) => {
    if (!email || email.trim() === '') return true; // Empty is valid (optional field)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const timeToMinutes = (timeString) => {
    if (!timeString) return 0;

    // Handle both "HH:MM" and "HH:MM AM/PM" formats
    const cleanTime = timeString.trim().toLowerCase().replace(/\s+/g, '');

    let hours = 0;
    let minutes = 0;
    let isPM = cleanTime.includes('pm');

    const match = cleanTime.match(/(\d{1,2}):?(\d{2})?/);
    if (match) {
      hours = parseInt(match[1], 10);
      minutes = match[2] ? parseInt(match[2], 10) : 0;

      // Convert to 24-hour format
      if (isPM && hours < 12) hours += 12;
      if (!isPM && hours === 12) hours = 0;
    }

    return hours * 60 + minutes;
  };

  // Form validation function
  const validateForm = () => {
    const newErrors = {};

    // Required: Status (must have at least one)
    if (!formData.status || (Array.isArray(formData.status) && formData.status.length === 0)) {
      newErrors.status = 'Please select at least one camp status';
    }

    // Required: Camp Name
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Camp name is required';
    }

    // Optional but validated if provided: Website
    if (formData.website && formData.website.trim() !== '' && !isValidUrl(formData.website)) {
      newErrors.website = 'Please enter a valid URL (e.g., https://example.com)';
    }

    // Optional but validated if provided: Email
    if (formData.email && formData.email.trim() !== '' && !isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Optional but validated if provided: Cost
    if (formData.price && formData.price.toString().trim() !== '') {
      const priceValue = parseFloat(formData.price);
      if (isNaN(priceValue) || priceValue < 0) {
        newErrors.price = 'Please enter a valid cost (0 or greater)';
      }
    }

    // Optional but validated if provided: Date ranges
    // Check for empty strings explicitly (not just truthy checks) to handle cleared dates
    const hasStartDate = formData.camp_start_date && typeof formData.camp_start_date === 'string' && formData.camp_start_date.trim() !== '';
    const hasEndDate = formData.camp_end_date && typeof formData.camp_end_date === 'string' && formData.camp_end_date.trim() !== '';
    
    if (hasStartDate && hasEndDate) {
      // Validate date strings before creating Date objects
      const startDateStr = formData.camp_start_date.trim();
      const endDateStr = formData.camp_end_date.trim();
      
      // Only validate if both are valid YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(startDateStr) && /^\d{4}-\d{2}-\d{2}$/.test(endDateStr)) {
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);

        // Check for Invalid Date objects
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          if (endDate < startDate) {
            newErrors.camp_end_date = 'End date must be after start date';
          }
        }
        // If dates are invalid, let individual field validation handle it
      }
    }

    // Optional but validated if provided: Time ranges
    if (formData.session_start_time && formData.session_end_time) {
      const startTime = timeToMinutes(formData.session_start_time);
      const endTime = timeToMinutes(formData.session_end_time);

      if (startTime > 0 && endTime > 0 && endTime <= startTime) {
        newErrors.session_end_time = 'Pick-up time must be after drop-off time';
      }
    }

    // Required: Session name when status is 'considering' or 'booked' and dates are filled
    // Reuse hasStartDate and hasEndDate variables already declared above for date range validation
    const requiresSession = 
      (Array.isArray(formData.status) 
        ? formData.status.some(s => String(s).toLowerCase() === 'considering' || String(s).toLowerCase() === 'booked')
        : String(formData.status).toLowerCase() === 'considering' || String(formData.status).toLowerCase() === 'booked') &&
      hasStartDate &&
      hasEndDate;

    if (requiresSession && !formData.session?.trim()) {
      newErrors.session = 'Session name is required when dates are added for considering or booked camps';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Update field helper
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // If clearing a date field, also clear session error if dates are no longer both present
    if ((field === 'camp_start_date' || field === 'camp_end_date') && (!value || (typeof value === 'string' && value.trim() === ''))) {
      setErrors((prev) => {
        if (prev.session) {
          const newErrors = { ...prev };
          delete newErrors.session;
          return newErrors;
        }
        return prev;
      });
    }
  };

  // Toggle status in local state (no auto-save - changes only persist when user clicks Save)
  const toggleStatus = (statusValue) => {
    // Prevent booking parent camps
    if (isParentCamp && statusValue === 'booked') {
      toast({
        title: 'Cannot book parent camp',
        description: 'Please select a specific session to book',
        variant: 'destructive',
      });
      return;
    }

    // Calculate new statuses
    const currentStatuses = Array.isArray(formData.status) ? formData.status : [formData.status].filter(Boolean);
    const normalizedValue = String(statusValue).toLowerCase();
    const isSelected = currentStatuses.some(s => String(s).toLowerCase() === normalizedValue);
    
    let newStatuses;
    if (isSelected) {
      // Remove status if already selected
      newStatuses = currentStatuses.filter(s => String(s).toLowerCase() !== normalizedValue);
      // Ensure at least one status remains
      if (newStatuses.length === 0) {
        newStatuses = ['saved']; // Default to saved if all are removed
      }
    } else {
      // Add status if not selected
      newStatuses = [...currentStatuses, normalizedValue];
    }

    // Update local state only (no database save until user clicks Save)
    setFormData((prev) => ({ ...prev, status: newStatuses }));
    
    // Clear error for status if it exists
    if (errors.status) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.status;
        return newErrors;
      });
    }
  };

  // Toggle child selection
  const toggleChild = (childName) => {
    setFormData((prev) => {
      const currentChildren = prev.child_names || [];
      const isSelected = currentChildren.includes(childName);

      return {
        ...prev,
        child_names: isSelected
          ? currentChildren.filter((name) => name !== childName)
          : [...currentChildren, childName],
      };
    });
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    validate: () => {
      const isValid = validateForm();
      if (!isValid) {
        // Scroll to first error after state update
        setTimeout(() => {
          const firstErrorField = Object.keys(errors)[0];
          if (firstErrorField) {
            const element = document.getElementById(firstErrorField) || 
                           document.getElementById('camp-name') || 
                           document.querySelector('[data-error-field]');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setTimeout(() => element.focus(), 300);
            }
          }
        }, 100);
      }
      return isValid;
    },
    getData: () => {
      // Ensure status is always an array
      const statusArray = Array.isArray(formData.status) 
        ? formData.status 
        : formData.status 
          ? [formData.status] 
          : ['saved'];
      
      return {
        status: statusArray, // Store as array in status field
        child_names: formData.child_names,
        program_data: {
          ...programData,
          name: formData.name,
          organization: formData.organization,
          location: formData.location,
          age_range: formData.age_range,
          grade_range: formData.grade_range,
          description: formData.description,
          editorial_summary: formData.description,
          brief_description: formData.description,
          camp_start_date: sanitizeDateForSave(formData.camp_start_date),
          camp_end_date: sanitizeDateForSave(formData.camp_end_date),
          registration_open_date: sanitizeDateForSave(formData.registration_open_date),
          early_registration_open_date: sanitizeDateForSave(formData.early_registration_open_date),
          registration_deadline: sanitizeDateForSave(formData.registration_deadline),
          early_bird_deadline: sanitizeDateForSave(formData.early_bird_deadline),
          cancellation_deadline: sanitizeDateForSave(formData.cancellation_deadline),
          session_start_time: formData.session_start_time,
          session_end_time: formData.session_end_time,
          schedule_text: formData.schedule_text,
          price: formData.price,
          price_period: formData.price_period,
          price_notes: formData.price_notes,
          website: formData.website,
          phone: formData.phone,
          email: formData.email,
          refund_policy: formData.refund_policy,
          notes: formData.notes,
          booking_confirmation: formData.booking_confirmation,
          selected_statuses: statusArray, // Also store in program_data for backward compatibility
          // Program/Session hierarchy fields
          program: formData.program || null,
          session: formData.session || null,
          session_year: formData.sessionYear || null,
        },
      };
    },
  }), [formData, programData, errors]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent submission if triggered by a date input change
    // This can happen when clearing a date field in some browsers
    if (e.target && (e.target.type === 'date' || e.target.type === 'time')) {
      return;
    }

    // Validate form
    const isValid = validateForm();
    if (!isValid) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField) || 
                       document.getElementById(`camp-name`) || 
                       document.querySelector('[data-error-field]');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => element.focus(), 300);
        }
      }
      return;
    }

    // Prepare data for save
    try {
      const updatedData = {
        ...userProgram,
        status: formData.status,
        child_names: formData.child_names,
        program_data: {
          ...programData,
          name: formData.name,
          organization: formData.organization,
          location: formData.location,
          age_range: formData.age_range,
          grade_range: formData.grade_range,
          description: formData.description,
          editorial_summary: formData.description,
          brief_description: formData.description,
          camp_start_date: sanitizeDateForSave(formData.camp_start_date),
          camp_end_date: sanitizeDateForSave(formData.camp_end_date),
          registration_open_date: sanitizeDateForSave(formData.registration_open_date),
          early_registration_open_date: sanitizeDateForSave(formData.early_registration_open_date),
          registration_deadline: sanitizeDateForSave(formData.registration_deadline),
          early_bird_deadline: sanitizeDateForSave(formData.early_bird_deadline),
          cancellation_deadline: sanitizeDateForSave(formData.cancellation_deadline),
          session_start_time: formData.session_start_time,
          session_end_time: formData.session_end_time,
          schedule_text: formData.schedule_text,
          price: formData.price,
          price_period: formData.price_period,
          price_notes: formData.price_notes,
          website: formData.website,
          phone: formData.phone,
          email: formData.email,
          refund_policy: formData.refund_policy,
          notes: formData.notes,
          booking_confirmation: formData.booking_confirmation,
          // Program/Session hierarchy fields
          program: formData.program || null,
          session: formData.session || null,
          session_year: formData.sessionYear || null,
        },
      };

      if (onSave) {
        await onSave(updatedData);
      }
    } catch (error) {
      console.error('Save failed:', error);
      // Show error message
      setErrors({
        _form: 'Failed to save changes. Please try again.',
      });
    }
  };

  // Status configuration
  const statusConfig = {
    favorite: {
      bg: 'bg-white',
      activeBg: 'bg-pink-500',
      border: 'border-pink-200',
      activeBorder: 'border-pink-600',
      text: 'text-pink-600',
      activeText: 'text-white',
      icon: Heart,
      label: 'Favorite',
    },
    booked: {
      bg: 'bg-white',
      activeBg: 'bg-purple-500',
      border: 'border-purple-200',
      activeBorder: 'border-purple-600',
      text: 'text-purple-600',
      activeText: 'text-white',
      icon: CheckCircle,
      label: 'Booked',
    },
    considering: {
      bg: 'bg-white',
      activeBg: 'bg-yellow-500',
      border: 'border-yellow-200',
      activeBorder: 'border-yellow-600',
      text: 'text-yellow-600',
      activeText: 'text-white',
      icon: Clock,
      label: 'Considering',
    },
    attended: {
      bg: 'bg-white',
      activeBg: 'bg-purple-500',
      border: 'border-purple-200',
      activeBorder: 'border-purple-600',
      text: 'text-purple-600',
      activeText: 'text-white',
      icon: CalendarCheck,
      label: 'Attended',
    },
    saved: {
      bg: 'bg-white',
      activeBg: 'bg-amber-500',
      border: 'border-amber-200',
      activeBorder: 'border-amber-600',
      text: 'text-amber-600',
      activeText: 'text-white',
      icon: Bookmark,
      label: 'Saved',
    },
    recommended: {
      bg: 'bg-white',
      activeBg: 'bg-indigo-500',
      border: 'border-indigo-200',
      activeBorder: 'border-indigo-600',
      text: 'text-indigo-600',
      activeText: 'text-white',
      icon: ThumbsUp,
      label: 'Recommended',
    },
  };

  // StatusButton component
  const StatusButton = ({ status, active, onClick, label }) => {
    const config = statusConfig[status];
    if (!config) return null;

    // For booked status, use Check icon when active for better visibility
    const Icon = status === 'booked' && active ? Check : config.icon;

    // Get ring color class based on status
    const ringColorClass = active ? {
      favorite: 'ring-pink-500',
      booked: 'ring-purple-500',
      considering: 'ring-yellow-500',
      attended: 'ring-purple-500',
      saved: 'ring-amber-500',
      recommended: 'ring-indigo-500',
    }[status] || '' : '';

    // Icon color classes - keep icons colorful
    const iconColorClass = active 
      ? 'text-white'
      : config.text; // Use the status color for inactive icons

    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 font-medium text-sm transition-all',
          active
            ? `${config.activeBg} ${config.activeBorder} shadow-md ring-2 ${ringColorClass} ring-offset-1`
            : `${config.bg} ${config.border} hover:bg-gray-50 hover:border-gray-300`
        )}
      >
        <Icon className={cn(
          'w-4 h-4',
          iconColorClass,
          active && status !== 'booked' && 'fill-current',
          active && status === 'booked' && 'stroke-[3]'
        )} />
        <span className={active ? 'text-white' : 'text-black'}>
          {label}
        </span>
      </button>
    );
  };

  // ChildSelectionCard component
  const ChildSelectionCard = ({ children, selected, onClick }) => {
    return (
      <div
        onClick={onClick}
        className={cn(
          'flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition',
          selected
            ? 'bg-purple-50 border-purple-400'
            : 'bg-white border-gray-200 hover:border-gray-300'
        )}
      >
        {children}
      </div>
    );
  };

  // DateInput component - converts date strings to YYYY-MM-DD format for native input
  const DateInput = ({ id, value, onChange, placeholder }) => {
    // Convert date string to YYYY-MM-DD format for native date input
    // IMPORTANT: Parse dates without timezone conversion to avoid day-shift issues
    const formatDateForInput = (dateString) => {
      // Handle all falsy values (null, undefined, empty string, 0, false)
      if (!dateString || dateString === null || dateString === undefined) {
        return '';
      }
      
      // Convert to string and trim whitespace
      const dateStr = String(dateString).trim();
      
      // Return empty string if result is empty after trimming
      if (dateStr === '') {
        return '';
      }
      
      try {
        // If already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return dateStr;
        }
        
        // Try to parse as ISO date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
        // Extract date part only to avoid timezone conversion
        const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
          const [, year, month, day] = isoMatch;
          // Validate the date components
          const yearNum = parseInt(year, 10);
          const monthNum = parseInt(month, 10);
          const dayNum = parseInt(day, 10);
          // Validate the date is reasonable
          if (yearNum >= 1900 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
            return `${year}-${month}-${day}`;
          }
        }
        
        // Try to parse as other date formats (MM/DD/YYYY, etc.)
        // Parse as local date to avoid timezone shifts
        // IMPORTANT: For date-only strings, parse as local date, not UTC
        let date;
        if (typeof dateStr === 'string' && dateStr.includes('/')) {
          // Handle MM/DD/YYYY format - parse as local date
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0], 10) - 1; // month is 0-indexed
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);
            // Validate parsed values before creating Date
            if (!isNaN(year) && !isNaN(month) && !isNaN(day) && year >= 1900 && year <= 2100) {
              date = new Date(year, month, day);
            } else {
              return '';
            }
          } else {
            // Try parsing as-is, but validate first
            date = new Date(dateStr);
          }
        } else {
          // For other formats, try to extract date components first
          const dateMatch = dateStr.match(/(\d{4})[-\/](\d{2})[-\/](\d{2})/);
          if (dateMatch) {
            const [, year, month, day] = dateMatch;
            const yearNum = parseInt(year, 10);
            const monthNum = parseInt(month, 10);
            const dayNum = parseInt(day, 10);
            // Validate before creating Date
            if (!isNaN(yearNum) && !isNaN(monthNum) && !isNaN(dayNum) && yearNum >= 1900 && yearNum <= 2100) {
              date = new Date(yearNum, monthNum - 1, dayNum);
            } else {
              return '';
            }
          } else {
            // Try parsing as-is, but validate result
            date = new Date(dateStr);
          }
        }
        
        // Validate the Date object before using it
        if (!date || isNaN(date.getTime())) {
          return '';
        }
        
        // Extract date components directly from the parsed date
        // This ensures we get the local date, not UTC
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        // Return empty string for any error to prevent crashes
        console.warn('Error formatting date for input:', error);
        return '';
      }
    };

    const handleChange = (e) => {
      // Prevent any default browser behavior that might cause page reload
      e.preventDefault();
      e.stopPropagation();
      
      // Safely get input value, handling null/undefined
      const inputValue = e?.target?.value || '';
      
      // Native date input returns YYYY-MM-DD format or empty string when cleared
      // Ensure we only pass valid YYYY-MM-DD strings, never Date objects or ISO strings
      if (inputValue && typeof inputValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(inputValue.trim())) {
        onChange(inputValue.trim());
      } else {
        // User cleared the date - pass empty string
        onChange('');
      }
    };

    const handleClick = (e) => {
      // Prevent any click events from bubbling up and potentially closing edit mode
      e.stopPropagation();
    };

    const handleFocus = (e) => {
      // Prevent focus events from bubbling up
      e.stopPropagation();
    };

    const handleBlur = (e) => {
      // Prevent blur events from bubbling up
      e.stopPropagation();
    };

    // Ensure the value is always a clean YYYY-MM-DD string for the date input
    const cleanValue = formatDateForInput(value);
    
    return (
      <input
        id={id}
        type="date"
        value={cleanValue}
        onChange={handleChange}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          // Prevent Enter key from submitting the form
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
      />
    );
  };

  // TimeInput component - handles time input in HH:MM format
  const TimeInput = ({ id, value, onChange, placeholder }) => {
    // Convert time string to HH:MM format for native time input
    const formatTimeForInput = (timeString) => {
      if (!timeString) return '';
      // If already in HH:MM format, return as is
      if (/^\d{2}:\d{2}$/.test(timeString)) {
        return timeString;
      }
      // Try to parse 12-hour format (e.g., "9:00 AM")
      try {
        const [time, period] = timeString.split(' ');
        if (time && period) {
          const [hours, minutes] = time.split(':');
          let hour24 = parseInt(hours, 10);
          if (period.toUpperCase() === 'PM' && hour24 !== 12) {
            hour24 += 12;
          } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
            hour24 = 0;
          }
          return `${String(hour24).padStart(2, '0')}:${minutes || '00'}`;
        }
      } catch (error) {
        // If parsing fails, return empty
      }
      return '';
    };

    const handleChange = (e) => {
      const inputValue = e.target.value;
      // Native time input returns HH:MM format (24-hour)
      onChange(inputValue || '');
    };

    const handleClick = (e) => {
      // Prevent any click events from bubbling up and potentially closing edit mode
      e.stopPropagation();
    };

    const handleFocus = (e) => {
      // Prevent focus events from bubbling up
      e.stopPropagation();
    };

    const handleBlur = (e) => {
      // Prevent blur events from bubbling up
      e.stopPropagation();
    };

    return (
      <input
        id={id}
        type="time"
        value={formatTimeForInput(value)}
        onChange={handleChange}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
      />
    );
  };

  // Prevent form submission on Enter key in time/date inputs
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && (e.target.type === 'time' || e.target.type === 'date')) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <>
      <form 
        id="edit-program-form" 
        onSubmit={handleSubmit} 
        onKeyDown={handleFormKeyDown}
        className="max-w-4xl mx-auto px-6 py-8"
      >
      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <ExclamationCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-900 mb-2">
                Please fix the following errors:
              </h4>
              <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                {Object.entries(errors)
                  .filter(([key]) => key !== '_form')
                  .map(([field, message]) => (
                    <li key={field}>{message}</li>
                  ))}
              </ul>
              {errors._form && (
                <div className="mt-2 text-sm text-red-800 font-medium">{errors._form}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Private-only: Status */}
      {showPrivateSections && (
        <div className="mb-8">
          <Label className="block text-sm font-medium text-gray-900 mb-3">
            Status{' '}
            <span className="text-red-500">*</span>
          </Label>
          {errors.status && (
            <p className="text-sm text-red-600 mb-2">{errors.status}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {(() => {
              const allowedStatuses = getAllowedStatuses();
              const statusButtons = [
                { status: 'favorite', label: 'Favorite' },
                { status: 'booked', label: 'Booked' },
                { status: 'considering', label: 'Considering' },
                { status: 'attended', label: 'Attended' },
                { status: 'saved', label: 'Saved' },
                { status: 'recommended', label: 'Recommended' },
              ];

              return statusButtons
                .filter(btn => allowedStatuses.includes(btn.status))
                .map(btn => (
                  <StatusButton
                    key={btn.status}
                    status={btn.status}
                    active={Array.isArray(formData.status) 
                      ? formData.status.some(s => String(s).toLowerCase() === btn.status)
                      : String(formData.status).toLowerCase() === btn.status}
                    onClick={() => {
                      // Prevent booking parent camps
                      if (isParentCamp && btn.status === 'booked') {
                        toast({
                          title: 'Cannot book parent camp',
                          description: 'Please select a specific session to book',
                          variant: 'destructive',
                        });
                        return;
                      }
                      toggleStatus(btn.status);
                    }}
                    label={btn.label}
                  />
                ));
            })()}
          </div>
          {/* Show hint when Booked is not available for parent camps */}
          {isParentCamp && (
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <span>💡</span>
              <span>Add a session to your summer plan</span>
            </p>
          )}
        </div>
      )}

      {/* Accordion Sections */}
      <Accordion
        type="multiple"
        defaultValue={showPublicSections ? ['basic'] : ['booking']}
        className="space-y-4"
      >
        {/* Basic Information Section (Public) */}
        {showPublicSections && (
        <AccordionItem value="basic" className="border border-gray-200 rounded-lg overflow-hidden">
          <AccordionTrigger className="flex items-center gap-3 py-4 hover:bg-gray-50 px-4 transition">
            <FileText className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <span className="font-semibold text-gray-900 flex-1 text-left">
              Basic Information
            </span>
          </AccordionTrigger>

          <AccordionContent className="px-4 pb-6 space-y-4">
            {/* Camp Name */}
            <div className="space-y-2">
              <Label htmlFor="camp-name" className="block text-sm font-medium text-gray-900">
                Camp Name{' '}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="camp-name"
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Enter camp name"
                className={cn(
                  'w-full',
                  errors.name && 'border-red-500 focus-visible:ring-red-500'
                )}
                data-error-field={errors.name ? 'true' : undefined}
              />
              {errors.name && (
                <div className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <ExclamationCircle className="w-4 h-4" />
                  {errors.name}
                </div>
              )}
            </div>

            {/* Organization */}
            <div className="space-y-2">
              <Label htmlFor="organization" className="block text-sm font-medium text-gray-900">
                Organization
              </Label>
              <Input
                id="organization"
                type="text"
                value={formData.organization}
                onChange={(e) => updateField('organization', e.target.value)}
                placeholder="Enter organization name"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                The organization or company running this camp
              </p>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location" className="block text-sm font-medium text-gray-900">
                Location
              </Label>
              <Input
                id="location"
                type="text"
                value={formData.location}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder="Enter full address or city"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Full address or city where camp is located
              </p>
            </div>

            {/* Age Range */}
            <div className="space-y-2">
              <Label htmlFor="age-range" className="block text-sm font-medium text-gray-900">
                Age Range
              </Label>
              <Input
                id="age-range"
                type="text"
                value={formData.age_range}
                onChange={(e) => updateField('age_range', e.target.value)}
                placeholder="e.g., 6-12 years"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Appropriate age range for this camp
              </p>
            </div>

            {/* Grade Range (rising grade) */}
            <div className="space-y-2">
              <Label htmlFor="grade-range" className="block text-sm font-medium text-gray-900">
                Grade range (rising grade)
              </Label>
              <Input
                id="grade-range"
                type="text"
                value={formData.grade_range}
                onChange={(e) => updateField('grade_range', e.target.value)}
                placeholder="e.g., Rising K–5th or Entering grades 2–6"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Grade the child will be entering in the fall after summer
              </p>
            </div>

            {/* About This Program / Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="block text-sm font-medium text-gray-900">
                About This Program
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Enter a description of the program, activities, and what makes it special..."
                rows={6}
                className="w-full resize-y min-h-[120px]"
              />
              <p className="text-xs text-gray-500 mt-1">
                Description of the program that will appear in the "About This Program" section
              </p>
            </div>

            {/* Contact Information */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Mail className="w-4 h-4 text-purple-600" />
                Contact Information
              </h3>
              
              <div className="space-y-4">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="block text-sm font-medium text-gray-900">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="camp@example.com"
                    className={cn(
                      'w-full',
                      errors.email && 'border-red-500 focus-visible:ring-red-500'
                    )}
                  />
                  {errors.email && (
                    <div className="text-sm text-red-600 mt-1 flex items-center gap-1">
                      <ExclamationCircle className="w-4 h-4" />
                      {errors.email}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Camp contact email address
                  </p>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="block text-sm font-medium text-gray-900">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Camp contact phone number
                  </p>
                </div>

                {/* Website */}
                <div className="space-y-2">
                  <Label htmlFor="website" className="block text-sm font-medium text-gray-900">
                    Website
                  </Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="https://www.example.com"
                    className={cn(
                      'w-full',
                      errors.website && 'border-red-500 focus-visible:ring-red-500'
                    )}
                  />
                  {errors.website && (
                    <div className="text-sm text-red-600 mt-1 flex items-center gap-1">
                      <ExclamationCircle className="w-4 h-4" />
                      {errors.website}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Camp website URL (e.g., https://www.example.com)
                  </p>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Sessions List Section (Private) - Show when status is Saved/Recommended/Favorite */}
        {showPrivateSections && showSessionsList() && isBaseCamp && baseCampId && (
        <AccordionItem value="sessions" className="border border-gray-200 rounded-lg overflow-hidden">
          <AccordionTrigger className="flex items-center gap-3 py-4 hover:bg-gray-50 px-4 transition">
            <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <span className="font-semibold text-gray-900 flex-1 text-left">
              Available Sessions
            </span>
          </AccordionTrigger>

          <AccordionContent className="px-4 pb-6 space-y-6">
            <SessionList
              baseCampId={baseCampId}
              sessions={sessions}
              onAddSession={() => {
                setShowAddSessionModal(true);
              }}
              onEditSession={(sessionId, session) => {
                if (session?.from_user_camp_sessions) {
                  const parentCampId = session.parent_camp_id || baseCampId;
                  if (parentCampId) {
                    window.location.href = `/program/${parentCampId}?mode=user_program`;
                  } else {
                    console.error('Cannot edit session: parent camp ID not found', session);
                    alert('Unable to edit session: parent camp not found');
                  }
                } else if (session?.from_camp_audit_data) {
                  window.location.href = `/program/${sessionId}`;
                } else {
                  window.location.href = `/program/${sessionId}?mode=user_program`;
                }
              }}
              onDeleteSession={async () => {
                await loadSessions();
              }}
              onRefresh={async () => {
                await loadSessions();
              }}
            />
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Booking Information Section (Private) - Only show when status is Considering or Booked */}
        {showPrivateSections && showBookingInfo() && (
        <AccordionItem value="booking" className="border border-gray-200 rounded-lg overflow-hidden">
          <AccordionTrigger className="flex items-center gap-3 py-4 hover:bg-gray-50 px-4 transition">
            <Users className="w-5 h-5 text-brand-600 flex-shrink-0" />
            <span className="font-semibold text-gray-900 flex-1 text-left">
              Booking Information
            </span>
          </AccordionTrigger>

          <AccordionContent className="px-4 pb-6 space-y-6">
            {/* Children Attending */}
            <div className="space-y-2">
              <Label className="block text-sm font-medium text-gray-900">
                Children Attending
              </Label>
              {profile?.children && profile.children.length > 0 ? (
                <>
                  <div className="grid gap-3 mt-3">
                    {profile.children.map((child) => {
                      const isSelected = formData.child_names.includes(child.name);

                      return (
                        <ChildSelectionCard
                          key={child.id || child.name}
                          selected={isSelected}
                          onClick={() => toggleChild(child.name)}
                        >
                          <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleChild(child.name)}
                              className="flex-shrink-0"
                            />
                          </div>

                          <Avatar className="w-12 h-12 rounded-full bg-purple-100 flex-shrink-0">
                            {child.photo_url ? (
                              <AvatarImage src={child.photo_url} alt={child.name} />
                            ) : null}
                            <AvatarFallback className="bg-brand-100 text-brand-600 font-semibold text-sm">
                              {child.name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>

                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 text-sm">
                              {child.name}
                            </div>
                            {child.age && (
                              <div className="text-sm text-gray-600">Age {child.age}</div>
                            )}
                          </div>
                        </ChildSelectionCard>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Select which children will attend this camp
                  </p>
                </>
              ) : (
                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">
                    No children found in your profile. Add children in your profile settings.
                  </p>
                </div>
              )}
            </div>


            {/* Program/Session Form - Show when status is 'considering' or 'booked' and dates are filled */}
            {(() => {
              // Check for empty strings explicitly (not just truthy checks) to handle cleared dates
              const hasStartDate = formData.camp_start_date && typeof formData.camp_start_date === 'string' && formData.camp_start_date.trim() !== '';
              const hasEndDate = formData.camp_end_date && typeof formData.camp_end_date === 'string' && formData.camp_end_date.trim() !== '';
              
              const requiresSession = 
                (Array.isArray(formData.status) 
                  ? formData.status.some(s => String(s).toLowerCase() === 'considering' || String(s).toLowerCase() === 'booked')
                  : String(formData.status).toLowerCase() === 'considering' || String(formData.status).toLowerCase() === 'booked') &&
                hasStartDate &&
                hasEndDate;

              if (!requiresSession) return null;

              return (
                <div className="space-y-3 pt-1 border-t border-gray-200 mt-4">
                  <div className="space-y-2">
                    <Label className="block text-sm font-medium text-gray-900">
                      Session Name <span className="text-red-500">*</span>
                    </Label>
                    <ProgramSessionForm
                      program={formData.program}
                      session={formData.session}
                      sessionYear={formData.sessionYear || getCurrentSessionYear()}
                      onChange={(data) => {
                        setFormData(prev => ({
                          ...prev,
                          program: data.program || '',
                          session: data.session || '',
                          sessionYear: data.sessionYear || null
                        }));
                        // Clear error when user starts typing
                        if (errors.session) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.session;
                            return newErrors;
                          });
                        }
                      }}
                    />
                    {errors.session && (
                      <p className="text-sm text-red-600 mt-1">{errors.session}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      Enter the specific session name (e.g., 'CIA Week 1', 'Team Camp')
                    </p>
                  </div>

                  {/* Session Price */}
                  <div className="space-y-2">
                    <Label htmlFor="session-price" className="block text-sm font-medium text-gray-900">
                      Session Price (Optional)
                    </Label>
                    <Input
                      id="session-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.session_price || ''}
                      onChange={(e) => updateField('session_price', e.target.value)}
                      placeholder="0.00"
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500">
                      Enter the price for this specific session
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Session List - Show for base camps */}
            {isBaseCamp && baseCampId && (
              <div className="space-y-3 pt-1 border-t border-gray-200 mt-4">
                <SessionList
                  baseCampId={baseCampId}
                  sessions={sessions}
                  onAddSession={() => {
                    setShowAddSessionModal(true);
                  }}
                  onEditSession={(sessionId, session) => {
                    // Check if this is a session from user_camp_sessions
                    // Sessions from user_camp_sessions have their own UUIDs that don't exist in user_programs
                    // so we need to navigate to the parent base camp instead
                    if (session?.from_user_camp_sessions) {
                      // Navigate to the parent base camp (camp_id from user_camp_sessions)
                      // The parent_camp_id is set to the camp_id value when loading sessions
                      const parentCampId = session.parent_camp_id || baseCampId;
                      if (parentCampId) {
                        // Navigate to parent camp - it will load sessions including this one
                        window.location.href = `/program/${parentCampId}?mode=user_program`;
                      } else {
                        console.error('Cannot edit session: parent camp ID not found', session);
                        alert('Unable to edit session: parent camp not found');
                      }
                    } else if (session?.from_camp_audit_data) {
                      // Load from camp_audit_data (default mode loads from camp_audit_data)
                      window.location.href = `/program/${sessionId}`;
                    } else {
                      // Load from user_programs (legacy sessions)
                      window.location.href = `/program/${sessionId}?mode=user_program`;
                    }
                  }}
                  onDeleteSession={async () => {
                    await loadSessions();
                  }}
                  onRefresh={async () => {
                    await loadSessions();
                  }}
                />
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Important Dates Section (Public) */}
        {showPublicSections && (
        <AccordionItem value="dates" className="border border-gray-200 rounded-lg overflow-hidden">
          <AccordionTrigger className="flex items-center gap-3 py-4 hover:bg-gray-50 px-4 transition">
            <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <span className="font-semibold text-gray-900 flex-1 text-left">
              Important Dates
            </span>
          </AccordionTrigger>

          <AccordionContent className="px-4 pb-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Registration Open Date */}
              <div className="space-y-2">
                <Label htmlFor="registration-open-date" className="block text-sm font-medium text-gray-900">
                  Registration Open Date
                </Label>
                <DateInput
                  id="registration-open-date"
                  value={formData.registration_open_date}
                  onChange={(date) => updateField('registration_open_date', date)}
                  placeholder="MM/DD/YYYY"
                />
                <p className="text-xs text-gray-500 mt-1">First day registration opens</p>
              </div>

              {/* Early Registration Open Date */}
              <div className="space-y-2">
                <Label htmlFor="early-registration-open-date" className="block text-sm font-medium text-gray-900">
                  Early Registration Open Date
                </Label>
                <DateInput
                  id="early-registration-open-date"
                  value={formData.early_registration_open_date}
                  onChange={(date) => updateField('early_registration_open_date', date)}
                  placeholder="MM/DD/YYYY"
                />
                <p className="text-xs text-gray-500 mt-1">First day early registration opens</p>
              </div>

              {/* Registration Deadline */}
              <div className="space-y-2">
                <Label htmlFor="registration-deadline" className="block text-sm font-medium text-gray-900">
                  Registration Deadline
                </Label>
                <DateInput
                  id="registration-deadline"
                  value={formData.registration_deadline}
                  onChange={(date) => updateField('registration_deadline', date)}
                  placeholder="MM/DD/YYYY"
                />
                <p className="text-xs text-gray-500 mt-1">Last day to register</p>
              </div>

              {/* Early Bird Deadline */}
              <div className="space-y-2">
                <Label htmlFor="early-bird-deadline" className="block text-sm font-medium text-gray-900">
                  Early Bird Deadline
                </Label>
                <DateInput
                  id="early-bird-deadline"
                  value={formData.early_bird_deadline}
                  onChange={(date) => updateField('early_bird_deadline', date)}
                  placeholder="MM/DD/YYYY"
                />
                <p className="text-xs text-gray-500 mt-1">Save on early registration</p>
              </div>

              {/* Cancellation Deadline */}
              <div className="space-y-2">
                <Label htmlFor="cancellation-deadline" className="block text-sm font-medium text-gray-900">
                  Cancellation Deadline
                </Label>
                <DateInput
                  id="cancellation-deadline"
                  value={formData.cancellation_deadline}
                  onChange={(date) => updateField('cancellation_deadline', date)}
                  placeholder="MM/DD/YYYY"
                />
                <p className="text-xs text-gray-500 mt-1">Last day for full refund</p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Refund Policy Section (Public) */}
        {showPublicSections && (
        <AccordionItem value="refund" className="border border-gray-200 rounded-lg overflow-hidden">
          <AccordionTrigger className="flex items-center gap-3 py-4 hover:bg-gray-50 px-4 transition">
            <RotateCcw className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <span className="font-semibold text-gray-900 flex-1 text-left">
              Refund Policy
            </span>
          </AccordionTrigger>

          <AccordionContent className="px-4 pb-6">
            <div className="space-y-2">
              <Label htmlFor="refund-policy" className="block text-sm font-medium text-gray-900">
                Refund Policy
              </Label>
              <Textarea
                id="refund-policy"
                value={formData.refund_policy}
                onChange={(e) => updateField('refund_policy', e.target.value)}
                placeholder="Enter the camp's refund and cancellation policy..."
                rows={5}
                className="w-full resize-y"
              />
              <p className="text-xs text-gray-500 mt-1">
                Camp's policy for cancellations, refunds, and withdrawals
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Additional Notes Section (Private) */}
        {showPrivateSections && (
        <AccordionItem value="notes" className="border border-gray-200 rounded-lg overflow-hidden">
          <AccordionTrigger className="flex items-center gap-3 py-4 hover:bg-gray-50 px-4 transition">
            <FileText className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <span className="font-semibold text-gray-900 flex-1 text-left">
              Additional Notes
            </span>
            <Badge variant="outline" className="ml-auto mr-2 text-xs px-2 py-1 border-gray-300 bg-white text-gray-700">
              <Lock className="w-3 h-3 mr-1" />
              Private
            </Badge>
          </AccordionTrigger>

          <AccordionContent className="px-4 pb-6">
            <div className="space-y-2">
              <Label htmlFor="notes" className="block text-sm font-medium text-gray-900">
                Your Private Notes
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                placeholder="Your personal notes about this camp (only visible to you)..."
                rows={6}
                className="w-full resize-y"
              />
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-purple-900">
                    <strong className="font-semibold">Private:</strong> These notes are only visible to you{' '}
                    and will never be shared with other users or the camp.
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        )}
      </Accordion>
      </form>

      {/* Add Session Modal */}
      <AddSessionModal
        isOpen={showAddSessionModal}
        onClose={() => setShowAddSessionModal(false)}
        parentCamp={{
          id: baseCampId || camp?.place_id || camp?.id || formData.place_id,
          place_id: camp?.place_id || camp?.id || userProgram?.program_data?.place_id || formData.place_id,
          name: camp?.name || formData.name || userProgram?.program_data?.name,
          organization: camp?.organization || formData.organization || userProgram?.program_data?.organization,
          program: camp?.program || formData.program || userProgram?.program_data?.program,
          source: camp?.source || formData.source || userProgram?.program_data?.source || 'user_entry',
          location: camp?.location || camp?.formatted_address || formData.location || userProgram?.program_data?.location,
        }}
        onSessionAdded={async (newSession) => {
          // Refresh sessions list
          await loadSessions();
        }}
      />
    </>
  );
});

EditProgramForm.displayName = 'EditProgramForm';

export default EditProgramForm;
